import React, { useState } from "react";
import { Layout, Table, Space, Button } from "antd";
import Surface from "../../components/layouts/Surface";
import { useIntl } from "react-intl";
import {
  listOrderImportsBatches,
  getOrderImportsBatch,
} from "../../api/server";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { PlusOutlined, EyeOutlined } from "@ant-design/icons";
import NewOrdersImport from "./components/NewOrdersImport";
import ImportResultsModal from "./components/ImportResultsModal";
import moment from "moment";
import RestrictedComponent from "../../components/util/RestrictedComponent";

const OrdersImport = () => {
  const intl = useIntl();
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const [uploadOrdersImportFileVisible, setUploadOrdersImportFileVisible] =
    useState(false);
  const [selectedBatchResults, setSelectedBatchResults] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const { isLoading, data } = useQuery(
    ["ordersImport", pagination.current, pagination.pageSize, scopePrefix],
    async () => {
      const result = await listOrderImportsBatches({
        current: pagination.current,
        pageSize: pagination.pageSize,
        scopePrefix,
        order: {
          field: "created_at",
          order: "descend",
        },
      });
      setPagination((prev) => ({
        ...prev,
        total: result.data.total,
      }));
      return result.data.items;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
    }
  );

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleViewBatch = async (batchId) => {
    try {
      const response = await getOrderImportsBatch(batchId);
      setSelectedBatchResults(response?.data?.lines || []);
    } catch (error) {
      console.error("Error fetching batch:", error);
    }
  };

  const columns = [
    {
      title: intl.formatMessage({
        id: "app.ordersImport.id",
      }),
      dataIndex: "id",
    },
    {
      title: intl.formatMessage({
        id: "app.ordersImport.createdAt",
      }),
      dataIndex: "created_at",
      render: (date) => moment(date).format("HH:mm:ss DD/MM/YYYY"),
    },
    {
      title: intl.formatMessage({
        id: "app.ordersImport.createdBy",
      }),
      dataIndex: ["created_by", "name"],
    },
    {
      title: intl.formatMessage({
        id: "app.ordersImport.actions",
      }),
      width: "130px",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewBatch(record.id)}
          >
            {intl.formatMessage({
              id: "app.ordersImport.viewBatch",
            })}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <Surface>
        <div>
          <RestrictedComponent
            resource={"order_import"}
            action={"editor"}
            comp={null}
          >
            <Button
              onClick={() => setUploadOrdersImportFileVisible(true)}
              type="primary"
              style={{
                marginBottom: "10px",
                display: "flex",
                alignItems: "center",
              }}
              icon={<PlusOutlined />}
            >
              {intl.formatMessage({
                id: "app.ordersImport.addNewOrderImport",
              })}
            </Button>
          </RestrictedComponent>
          <Table
            bordered
            dataSource={data || []}
            columns={columns}
            rowKey={(record) => record.id}
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{
              y: window.innerHeight - 320,
            }}
            loading={isLoading}
          />
        </div>
      </Surface>
      <NewOrdersImport
        visible={uploadOrdersImportFileVisible}
        hide={() => setUploadOrdersImportFileVisible(false)}
      />
      <ImportResultsModal
        visible={!!selectedBatchResults}
        onClose={() => setSelectedBatchResults(null)}
        results={selectedBatchResults || []}
      />
    </Layout>
  );
};

export default OrdersImport;
